import React, { Component, ErrorInfo, ReactNode } from 'react';
import './GlobalErrorBoundary.css';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    detailsVisible: boolean;
}

// Признаки ошибки из-за устаревших чанков после деплоя
const isChunkLoadError = (error: Error): boolean => {
    const msg = error?.message ?? '';
    const name = error?.name ?? '';
    return (
        msg.includes("Unexpected token '<'") ||
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('ChunkLoadError') ||
        name === 'ChunkLoadError'
    );
};

class GlobalErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            detailsVisible: false
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Если это ошибка устаревших чанков — делаем hard reload автоматически
        if (isChunkLoadError(error)) {
            // Предотвращаем бесконечный цикл: проверяем флаг в sessionStorage
            const alreadyReloaded = sessionStorage.getItem('chunk_reload_attempted');
            if (!alreadyReloaded) {
                sessionStorage.setItem('chunk_reload_attempted', '1');
                window.location.reload();
                // Возвращаем состояние без ошибки, чтобы не мигала страница ошибки
                return { hasError: false, error: null };
            }
        }
        return { hasError: true, error };
    }

    componentDidMount() {
        // Clear the reload flag on successful render so future chunk errors in the same session can auto-reload
        sessionStorage.removeItem('chunk_reload_attempted');
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ errorInfo });
        console.error('Uncaught error:', error, errorInfo);
    }

    handleReload = () => {
        sessionStorage.removeItem('chunk_reload_attempted');
        window.location.reload();
    };

    handleGoHome = () => {
        sessionStorage.removeItem('chunk_reload_attempted');
        // Hard reload на главную — сбрасывает кэш JS-чанков
        window.location.href = '/';
    };

    toggleDetails = () => {
        this.setState(prev => ({ detailsVisible: !prev.detailsVisible }));
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="error-boundary-container">
                    <div className="eb-bg-glow"></div>
                    <div className="eb-bg-glow secondary"></div>

                    <div className="eb-card">
                        <div className="eb-glitch-wrapper">
                            <div className="eb-glitch-icon">
                                <i className="fas fa-microchip"></i>
                                <div className="eb-glitch-overlay"></div>
                            </div>
                        </div>

                        <h1 className="eb-title">Упс! Что-то пошло не так</h1>
                        <p className="eb-subtitle">
                            Произошла непредвиденная ошибка в работе приложения.
                            Мы уже зафиксировали её и скоро всё починим.
                        </p>

                        <div className="eb-actions">
                            <button type="button" className="eb-btn primary" onClick={this.handleGoHome}>
                                <i className="fas fa-home"></i>
                                На главную
                            </button>
                            <button type="button" className="eb-btn secondary" onClick={this.handleReload}>
                                <i className="fas fa-redo"></i>
                                Перезагрузить
                            </button>
                        </div>

                        <div className="eb-details-section">
                            <button type="button" className="eb-details-toggle" onClick={this.toggleDetails}>
                                {this.state.detailsVisible ? 'Скрыть детали' : 'Показать детали для разработчиков'}
                                <i className={`fas fa-chevron-${this.state.detailsVisible ? 'up' : 'down'}`}></i>
                            </button>

                            {this.state.detailsVisible && (
                                <div className="eb-details-content">
                                    <div className="eb-error-message">
                                        <strong>Error:</strong> {this.state.error?.toString()}
                                    </div>
                                    <pre className="eb-error-stack">
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default GlobalErrorBoundary;
